import type { ReactNode } from 'react'

/** Shared data-table chrome for the ops console. Wraps the table in a bordered
 *  surface; head is a calm uppercase label row on the canvas; rows get a hairline
 *  separator and a hover wash. */
export function DataTable({
  children,
  className = '',
  label,
}: {
  children: ReactNode
  className?: string
  /** Accessible name for the table (maps to aria-label); omit if a nearby heading already names it. */
  label?: string
}) {
  return (
    <div className={`overflow-x-auto rounded-lg border border-line bg-surface ${className}`}>
      <table className="min-w-full text-sm" aria-label={label}>{children}</table>
    </div>
  )
}

/** The column-header row's classes, hoisted out of the component so the contrast
 *  test can measure what this table ACTUALLY renders rather than the palette in
 *  the abstract (the same reason CALLOUT_TONES is hoisted). Still a literal, so
 *  the Tailwind scanner sees every class.
 *
 *  `text-ink-soft`, not `text-ink-mut`: these are the labels naming the data
 *  beneath them, and at 12px they have to clear 4.5:1. ink-mut measures 3.49:1 on
 *  canvas — the identical mistake the Chips label used to make. */
export const TABLE_HEADER_CLASS =
  'border-b border-line bg-canvas px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-ink-soft'

const ARIA_SORT = { asc: 'ascending', desc: 'descending', none: 'none' } as const

export function Th({
  children,
  className = '',
  sort,
}: {
  children?: ReactNode
  className?: string
  /** Current sort state of this column; omit when the column is not sortable. */
  sort?: 'asc' | 'desc' | 'none'
}) {
  return (
    <th scope="col" aria-sort={sort ? ARIA_SORT[sort] : undefined} className={`${TABLE_HEADER_CLASS} ${className}`}>
      {children}
    </th>
  )
}

export function Td({
  children,
  className = '',
  colSpan,
}: {
  children?: ReactNode
  className?: string
  /** Span the remaining columns. ⚠️ Exists for the FULL-WIDTH DETAIL ROW pattern: a long
   *  message (an error, a diagnosis) is an annotation ON a row, not a column of data, and
   *  putting it in a column makes its longest line set the table's width. Given as a prop
   *  rather than left to a raw `<td>` so a detail row keeps this table's padding and
   *  hairline. */
  colSpan?: number
}) {
  return (
    <td colSpan={colSpan} className={`border-b border-line px-3 py-2 align-middle ${className}`}>
      {children}
    </td>
  )
}

export function Tr({
  children,
  className = '',
  muted = false,
}: {
  children: ReactNode
  className?: string
  muted?: boolean
}) {
  return (
    <tr className={`transition-colors hover:bg-canvas ${muted ? 'opacity-50' : ''} ${className}`}>
      {children}
    </tr>
  )
}
